import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { CenterSetting } from '@core/interfaces/center-setting';
import store from '@app/store';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import StripeConnectLink from '@shared/components/forms/inputs/stripe-connect/stripe-connect-link.vue';
import CustomContactFieldsHelper from './helpers/custom-contact-fields-helper';
import { AccessControlConfigInterface } from '@core/interfaces/access-control-config-interface';
import ModulesHelper from '@core/services/modules.helper';
import AccessControlConfigForm from '@shared/components/forms/inputs/access-control-config-form/access-control-config-form.vue';
import { setProp } from '@shared/helpers/operators';

library.add(faPlus);

@Component({
  name: 'bpc-center-settings',
  components: {
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-std-form': StdForm,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-stripe-connect-link': StripeConnectLink,
    'bpc-access-control-config-form': AccessControlConfigForm,
  },
})
export default class CenterSettings extends Vue {
  $refs!: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    stdForm: InstanceType<typeof StdForm> & { saveEntity(): void; formModel: any };
    deleteMediaFileConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    accessControlConfigForm: InstanceType<typeof AccessControlConfigForm> & {
      saveEntity(): void;
      onEntitySaved(e): void;
      formModel: any;
    };
  };

  public showEditForm: boolean = false;

  /**
   * Form
   */
  centerSetting: CenterSetting = new CenterSetting();
  originalAccessControlConfig: string = null;
  accessControlConfigFormModel: AccessControlConfigInterface = {
    id: null,
    access_control_service: null,
    access_control_key: null,
    access_control_url: null,
    early_access_delay: null,
  };

  // COMPUTED

  // Access Control Module
  get hasAccessControlModuleActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ACCESS_CONTROL_MODULE_NAME);
  }

  /* Lifecycle hooks */

  created() {
    this.onEdit();
  }

  onInputCustomEvent(event) {
    const eventName = event.eventName;
    switch (eventName) {
      case 'selectedCityFromPostalCode':
        // When updating the zip code field, update the city field
        setProp(this.$refs.stdForm?.formModel, 'city', event.value);
        break;
      case 'selectedCountryFromPostalCode':
        // When updating the zip code field, update the country field
        setProp(this.$refs.stdForm?.formModel, 'country', event.value.toLowerCase());
        break;
      default:
        break;
    }
  }

  mounted() {
    this.$root.$on('updateDefaultPriceCategoryForPostalCodes', event => {
      setProp(
        this.$refs.stdForm?.formModel,
        'default_price_category_id_for_postal_codes',
        event?.id ?? null,
      );
    });
  }
  destroyed() {
    this.$root.$off('updateDefaultPriceCategoryForPostalCodes');
  }

  // updated () { }
  // destroyed () { }

  onEdit() {
    this.centerSetting = new CenterSetting();
    this.showEditForm = true;
    this.centerSetting.id = store.getters['tenant/selectedTenant'].center_setting.id; // We have to determine the option id of the current center
  }

  async onEditFormSave() {
    console.log('Saving');
    // Prepare custom contact fields using a helper function
    const customContactFields = CustomContactFieldsHelper.prepareCustomContactFields(
      this.$refs.stdForm.formModel,
    );
    this.$refs.stdForm.formModel.custom_contact_fields = customContactFields;
    // Check if the access control module is activated
    if (this.hasAccessControlModuleActivated) {
      // Determine if the access control configuration has changed
      const accessControlConfigHasChanged = this.accessControlConfigHasChanged(
        this.$refs.accessControlConfigForm.formModel,
      );
      // If the configuration has changed, save the new configuration
      if (accessControlConfigHasChanged) {
        this.$refs.accessControlConfigForm.saveEntity();
      } else {
        // Save the center settings as normal
        this.$refs.stdForm.saveEntity();
      }
    } else {
      // Save the center settings as normal
      this.$refs.stdForm.saveEntity();
    }
  }

  // Save the center settings as normal after saving access control settings
  onConfigSaved(e) {
    this.$refs.stdForm.saveEntity();
  }

  onEntitySaved(e) {
    this.$emit('entity-saved', e);
    this.$store.commit('centerSettings/updateCenterSettings', this.$refs.stdForm.formModel);
    if (this.hasAccessControlModuleActivated) {
      this.$store.commit(
        'centerSettings/updateAccessControlConfig',
        this.$refs.accessControlConfigForm.formModel,
      );
    }
    this.$router.go(null);
  }

  getModalTitle() {
    return 'Edit Center Settings';
  }

  getDeleteMediaFileConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'media-file-delete-modal',
      title: this.$t('global.modals.media-delete.title') as string,
      body: this.$t('global.modals.media-delete.body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.permanent_delete') as string,
      acceptButtonVariant: 'danger',
    };
  }

  onSuccessMediaFileDelete() {
    this.$refs.deleteMediaFileConfirmationModal.onCloseModal();
  }

  showDeleteMediaFileModal(mediaId: string) {
    this.$refs.deleteMediaFileConfirmationModal.onOpenModal(mediaId);
  }

  onAccessControlConfigLoaded(formModel: AccessControlConfigInterface) {
    this.originalAccessControlConfig = [
      formModel.access_control_service,
      formModel.access_control_key,
      formModel.access_control_url,
      formModel.early_access_delay,
    ].join('--');
  }

  accessControlConfigHasChanged(formModel: AccessControlConfigInterface) {
    const parsed: string = [
      formModel.access_control_service,
      formModel.access_control_key,
      formModel.access_control_url,
      formModel.early_access_delay,
    ].join('--');
    return this.originalAccessControlConfig !== parsed;
  }
}
